import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Query,
  DocumentData,
  Timestamp,
} from "firebase/firestore";
import { db } from "./config";

/**
 * Helper pour convertir les timestamps Firestore en Date
 */
export function convertTimestamps(data: any): any {
  if (!data) return data;

  if (data instanceof Timestamp) {
    return data.toDate();
  }

  if (Array.isArray(data)) {
    return data.map(convertTimestamps);
  }

  if (typeof data === "object") {
    const converted: any = {};
    for (const [key, value] of Object.entries(data)) {
      converted[key] = convertTimestamps(value);
    }
    return converted;
  }

  return data;
}


/**
 * Récupérer plusieurs documents
 */
export async function getDocuments<T = any>(
  collectionName: string,
  constraints: Array<{ field: string; operator: any; value: any }> = [],
  orderByField?: string,
  orderDirection: "asc" | "desc" = "desc",
  limitCount?: number
): Promise<T[]> {
  let q: Query<DocumentData> = collection(db, collectionName);

  // Appliquer les contraintes
  for (const constraint of constraints) {
    q = query(q, where(constraint.field, constraint.operator, constraint.value));
  }

  // Appliquer l'ordre
  if (orderByField) {
    q = query(q, orderBy(orderByField, orderDirection));
  }

  // Appliquer la limite
  if (limitCount) {
    q = query(q, limit(limitCount));
  }

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) =>
    convertTimestamps({ id: doc.id, ...doc.data() })
  ) as T[];
}

/**
 * Créer un document
 */
export async function createDocument<T = any>(
  collectionName: string,
  data: Omit<T, "id" | "createdAt" | "updatedAt">
): Promise<string> {
  const docData = {
    ...data,
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  const docRef = await addDoc(collection(db, collectionName), docData);
  return docRef.id;
}

/**
 * Mettre à jour un document
 */
export async function updateDocument(
  collectionName: string,
  docId: string,
  data: Partial<any>
): Promise<void> {
  const docRef = doc(db, collectionName, docId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: new Date(),
  });
}

/**
 * Supprimer un document
 */
export async function deleteDocument(
  collectionName: string,
  docId: string
): Promise<void> {
  const docRef = doc(db, collectionName, docId);
  await deleteDoc(docRef);
}


/**
 * Écouter les changements d'une collection en temps réel
 */
export function subscribeToCollection<T = any>(
  collectionName: string,
  constraints: Array<{ field: string; operator: any; value: any }>,
  orderByField: string | undefined,
  orderDirection: "asc" | "desc",
  callback: (data: T[]) => void
): () => void {
  let q: Query<DocumentData> = collection(db, collectionName);

  for (const constraint of constraints) {
    q = query(q, where(constraint.field, constraint.operator, constraint.value));
  }

  if (orderByField) {
    q = query(q, orderBy(orderByField, orderDirection));
  }

  return onSnapshot(q, (querySnapshot) => {
    const data = querySnapshot.docs.map((doc) =>
      convertTimestamps({ id: doc.id, ...doc.data() })
    ) as T[];
    callback(data);
  });
}

